import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import {
  Plus,
  Pencil,
  Copy,
  Trash2,
  Search,
  Dumbbell,
  ChevronLeft,
  ChevronRight,
  Box,
} from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IBlock } from '../../../models/programming';
import { BLOCK_TYPE, BLOCK_TYPE_COLORS, BLOCK_TYPE_LABELS } from '../../../enums/programming';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import BlockBuilderModal from '../BlockBuilderModal/BlockBuilderModal';
import styles from './BlocksTab.module.scss';

const MAX_PREVIEW_EXERCISES = 3;

const BlocksTab = () => {
  const queryClient = useQueryClient();
  const { getProgrammingBlocks, duplicateProgrammingBlock, deleteProgrammingBlock } = useEndpoints();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<IBlock | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const blocksQuery = useQuery({
    queryKey: ['programming-blocks', debouncedSearch, typeFilter, page],
    queryFn: async () => {
      const { data } = await getProgrammingBlocks({
        search: debouncedSearch || undefined,
        type: typeFilter || undefined,
        isTemplate: true,
        page,
        limit: 20,
      });
      return data;
    },
    staleTime: 2 * 60 * 1000,
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => duplicateProgrammingBlock(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programming-blocks'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteProgrammingBlock(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['programming-blocks'] }),
  });

  const blocks = useMemo(() => blocksQuery.data?.docs ?? [], [blocksQuery.data]);
  const totalPages = blocksQuery.data?.totalPages ?? 1;
  const totalDocs = blocksQuery.data?.totalDocs ?? 0;

  const handleEdit = useCallback((block: IBlock) => {
    setEditingBlock(block);
    setBuilderOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setEditingBlock(null);
    setBuilderOpen(true);
  }, []);

  const handleBuilderClose = useCallback(() => {
    setBuilderOpen(false);
    setEditingBlock(null);
  }, []);

  return (
    <div className={styles.container}>
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterPills}>
          <button
            className={`${styles.filterPill} ${typeFilter === '' ? styles.filterPillActive : ''}`}
            onClick={() => { setTypeFilter(''); setPage(1); }}
          >
            Tutti
            {totalDocs > 0 && (
              <span className={styles.pillCount}>{totalDocs}</span>
            )}
          </button>
          {Object.values(BLOCK_TYPE).map((t) => (
            <button
              key={t}
              className={`${styles.filterPill} ${typeFilter === t ? styles.filterPillActive : ''}`}
              onClick={() => { setTypeFilter(t); setPage(1); }}
            >
              <span
                className={styles.pillDot}
                style={{ background: typeFilter === t ? '#fff' : BLOCK_TYPE_COLORS[t] }}
              />
              {BLOCK_TYPE_LABELS[t]}
            </button>
          ))}
        </div>
        <button className={styles.addBtn} onClick={handleNew}>
          <Plus size={15} strokeWidth={2.5} />
          Nuovo Blocco
        </button>
      </div>

      {/* Search */}
      <div className={styles.searchWrapper}>
        <Search className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Cerca blocco per nome..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className={styles.searchInput}
        />
      </div>

      {/* Content */}
      {blocksQuery.isLoading ? (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      ) : blocks.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrap}>
            <Box size={24} strokeWidth={1.5} />
          </div>
          <p className={styles.emptyTitle}>Nessun blocco trovato</p>
          <p className={styles.emptySubtitle}>
            {search || typeFilter
              ? 'Prova a modificare i filtri di ricerca'
              : 'Crea il tuo primo blocco per iniziare'}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {blocks.map((block) => {
              const blockColor = block.color || BLOCK_TYPE_COLORS[block.type] || '#757575';
              const badgeColor = BLOCK_TYPE_COLORS[block.type] || blockColor;
              const exercises = block.exercises ?? [];
              const previewExercises = exercises.slice(0, MAX_PREVIEW_EXERCISES);
              const remaining = exercises.length - MAX_PREVIEW_EXERCISES;

              return (
                <div key={block._id} className={styles.card}>
                  <div
                    className={styles.cardColorStrip}
                    style={{ background: blockColor }}
                  />
                  <div className={styles.cardBody}>
                    {/* Header */}
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitleArea}>
                        <p className={styles.blockTitle}>{block.title}</p>
                        <div className={styles.cardBadges}>
                          <span
                            className={styles.typeBadge}
                            style={{
                              background: badgeColor + '18',
                              color: badgeColor,
                              border: `1px solid ${badgeColor}30`,
                            }}
                          >
                            {BLOCK_TYPE_LABELS[block.type] || block.type}
                          </span>
                          {block.isLeopartTemplate && (
                            <span className={styles.leopartBadge}>Leopart</span>
                          )}
                        </div>
                      </div>
                      <div className={styles.cardActions}>
                        {!block.isLeopartTemplate && (
                          <button
                            className={styles.actionIconBtn}
                            onClick={() => handleEdit(block)}
                            title="Modifica"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        <button
                          className={styles.actionIconBtn}
                          onClick={() => duplicateMutation.mutate(block._id)}
                          title="Duplica"
                        >
                          <Copy size={14} />
                        </button>
                        {!block.isLeopartTemplate && (
                          <button
                            className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
                            onClick={() => {
                              if (window.confirm('Eliminare questo blocco?')) {
                                deleteMutation.mutate(block._id);
                              }
                            }}
                            title="Elimina"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Exercise preview */}
                    {previewExercises.length > 0 && (
                      <div className={styles.exercisePreview}>
                        {previewExercises.map((ex, i) => (
                          <div key={i} className={styles.exerciseItem}>
                            <span className={styles.exerciseOrder}>{ex.order ?? i + 1}</span>
                            <span className={styles.exerciseName}>
                              {(ex.exercise?.name as any)?.it || (ex.exercise?.name as any)?.en || '—'}
                            </span>
                            {ex.sets > 0 && (
                              <span className={styles.exerciseSets}>
                                {ex.sets}×{ex.reps || '—'}
                              </span>
                            )}
                          </div>
                        ))}
                        {remaining > 0 && (
                          <span className={styles.moreExercises}>
                            +{remaining} altr{remaining === 1 ? 'o' : 'i'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className={styles.cardFooter}>
                      <div className={styles.cardMeta}>
                        <span className={styles.metaItem}>
                          <Dumbbell size={13} strokeWidth={2.5} />
                          {exercises.length} eserciz{exercises.length === 1 ? 'io' : 'i'}
                        </span>
                      </div>
                      {block.tags.length > 0 && (
                        <div className={styles.cardTags}>
                          {block.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className={styles.tag}>{tag}</span>
                          ))}
                          {block.tags.length > 3 && (
                            <span className={styles.tag}>+{block.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.paginationRow}>
              <span className={styles.paginationInfo}>
                Pagina {page} di {totalPages}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageCounter}>{page} / {totalPages}</span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <BlockBuilderModal
        isOpen={builderOpen}
        onClose={handleBuilderClose}
        block={editingBlock}
      />
    </div>
  );
};

export default BlocksTab;
